/**
 * Chrome for the guest (shared-listing) area.
 *
 * Intentionally NOT RoleShell/Navigation: a guest has no account, so every
 * member affordance (sidebar, favorites, interests, profile) would be a dead
 * end. This shell carries only the brand, the recipient's identity, and the
 * one action that matters to them — joining.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { LogOut, Sparkles } from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { useGuest } from "@/contexts/GuestContext";

export default function GuestShell({
  children,
  showAccountBar = true,
}: {
  children: ReactNode;
  showAccountBar?: boolean;
}) {
  const { email, name, isVerified, endSession } = useGuest();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border/70 bg-card/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to={isVerified ? "/guest/listings" : "/"} className="flex items-center gap-2.5">
            <BrandLogo className="h-9 w-auto" />
          </Link>

          {showAccountBar && isVerified && (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden text-right sm:block">
                <div className="text-sm font-semibold text-foreground">{name || "Guest access"}</div>
                <div className="text-xs text-muted-foreground">{email}</div>
              </div>
              <Button asChild size="sm" className="shrink-0">
                <Link to="/register">
                  <Sparkles className="h-4 w-4" />
                  Join free
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={endSession}
                className="shrink-0 text-muted-foreground"
                title="End this guest session on this device"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Exit</span>
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="flex-1">{children}</div>

      <footer className="border-t border-border/70 bg-card/60 px-4 py-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} BusinessLocating Hub &mdash; We Find the Perfect Fit.
          </p>
          <p className="text-xs text-muted-foreground">
            These listings were shared with you in confidence. Please don&rsquo;t redistribute them.
          </p>
        </div>
      </footer>
    </div>
  );
}

/**
 * Watermark stamped across the guest listing view.
 *
 * The honest limit of the whole design: once a legitimate recipient can see a
 * listing, nothing stops them screenshotting it. Printing who the copy belongs
 * to makes a leaked screenshot traceable, which is the only real deterrent left.
 */
export function GuestWatermark({ email }: { email: string | null }) {
  if (!email) return null;
  const stamp = `${email} · ${new Date().toLocaleDateString()}`;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 select-none overflow-hidden opacity-[0.05]"
    >
      <div className="flex h-full w-full flex-wrap content-start gap-x-16 gap-y-20 p-8">
        {Array.from({ length: 24 }).map((_, i) => (
          <span
            key={i}
            className="whitespace-nowrap text-xs font-bold uppercase tracking-widest text-foreground"
            style={{ transform: "rotate(-24deg)" }}
          >
            {stamp}
          </span>
        ))}
      </div>
    </div>
  );
}
